import { setTraits, Transform, Mesh, type Plugin, type State, type System } from '@multiplekex/shallot';
import { Text } from '@multiplekex/shallot/extras';
import { sampleSpeculativeTokens } from './speedup-core';

// Configuration
const CUBE_LEFT_EDGE = -0.5;
const CUBE_RIGHT_EDGE = 0.5;
const BASE_SLIDE_TIME = 0.5;
const REMOVE_X = -30;
const SPAWN_X = 0;
const TOKEN_SPACING = 1.0;

// Text label config
const LABEL_X = 1.5;
const LABEL_POP_DURATION = 0.3;
const RATE_LABEL_X = 3.5;
const RATE_WINDOW = 10;

// Colors
const TARGET_TOKEN_COLOR = 0x4a90d9; // blue (sequential)
const SPEC_TOKEN_COLOR = 0x8b5cf6; // purple (speculative)

// Parameters (updated via setParams) - all latencies in milliseconds
let params = {
  k: 5,
  targetLatencyMs: 500,
  draftLatencyMs: 50,
  acceptanceProb: 0.6,
};

// Token component
export const Token = {
  id: [] as number[],
  baseX: [] as number[],
  beltId: [] as number[],
};

setTraits(Token, {
  defaults: () => ({ id: 0, baseX: 0, beltId: 0 }),
});

// Belt state
interface Belt {
  id: number;
  z: number;
  color: number;
  tokens: number[];
  getTokenCount: () => number;
  getInterval: () => number;
  timer: number;
  isSliding: boolean;
  slideStartTime: number;
  slideTime: number;
  slideAmount: number;
  labelEid: number;
  lastTokenCount: number;
  labelPopStartTime: number;
  rateValueEid: number;
  rateUnitEid: number;
  tokenHistory: { time: number; count: number }[];
}

let sequentialBelt: Belt;
let speculativeBelt: Belt;
let nextTokenId = 0;
let initialized = false;
let elapsedTime = 0;

// Bonus label state
const BONUS_DELAY = 0.05;
const BONUS_ANTICIPATION_DURATION = 0.04;
const BONUS_POP_DURATION = 0.15;
const BONUS_HOLD_DURATION = 0.3;
const BONUS_FADE_DURATION = 0.12;
let bonusLabelEid = -1;
let bonusStartTime = -1;

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function isInsideCube(x: number): boolean {
  return x > CUBE_LEFT_EDGE && x < CUBE_RIGHT_EDGE;
}

function sampleTokens(): number {
  return sampleSpeculativeTokens(params.k, params.acceptanceProb);
}

function createLabel(state: State, z: number, color: number): number {
  const eid = state.addEntity();
  state.addComponent(eid, Transform);
  state.addComponent(eid, Text);

  Transform.posX[eid] = LABEL_X;
  Transform.posY[eid] = 0;
  Transform.posZ[eid] = z;
  Transform.eulerX[eid] = -90;
  Transform.scaleX[eid] = 0;
  Transform.scaleY[eid] = 0;
  Transform.scaleZ[eid] = 0;

  Text.content[eid] = '';
  Text.fontSize[eid] = 0.8;
  Text.color[eid] = color;
  Text.anchorX[eid] = 0.5;
  Text.anchorY[eid] = 0.5;

  return eid;
}

function createBonusLabel(state: State): number {
  const eid = state.addEntity();
  state.addComponent(eid, Transform);
  state.addComponent(eid, Text);

  Transform.posX[eid] = 1.6;
  Transform.posY[eid] = 0;
  Transform.posZ[eid] = 2.7;
  Transform.eulerX[eid] = -90;
  Transform.scaleX[eid] = 0;
  Transform.scaleY[eid] = 0;
  Transform.scaleZ[eid] = 0;

  Text.content[eid] = 'bonus!';
  Text.fontSize[eid] = 0.4;
  Text.color[eid] = 0xc084fc;
  Text.anchorX[eid] = 0.5;
  Text.anchorY[eid] = 0.5;

  return eid;
}

function createRateLabels(
  state: State,
  z: number,
  color: number
): { valueEid: number; unitEid: number } {
  const valueEid = state.addEntity();
  state.addComponent(valueEid, Transform);
  state.addComponent(valueEid, Text);

  Transform.posX[valueEid] = RATE_LABEL_X;
  Transform.posY[valueEid] = 0;
  Transform.posZ[valueEid] = z;
  Transform.eulerX[valueEid] = -90;

  Text.content[valueEid] = '0.0';
  Text.fontSize[valueEid] = 0.6;
  Text.color[valueEid] = color;
  Text.anchorX[valueEid] = 1;
  Text.anchorY[valueEid] = 0.5;

  const unitEid = state.addEntity();
  state.addComponent(unitEid, Transform);
  state.addComponent(unitEid, Text);

  Transform.posX[unitEid] = RATE_LABEL_X + 0.1;
  Transform.posY[unitEid] = 0;
  Transform.posZ[unitEid] = z;
  Transform.eulerX[unitEid] = -90;

  Text.content[unitEid] = 'tok/s';
  Text.fontSize[unitEid] = 0.4;
  Text.color[unitEid] = 0x666666;
  Text.anchorX[unitEid] = 0;
  Text.anchorY[unitEid] = 0.5;

  return { valueEid, unitEid };
}

function createToken(state: State, belt: Belt, x: number): number {
  const eid = state.addEntity();
  state.addComponent(eid, Transform);
  state.addComponent(eid, Mesh);
  state.addComponent(eid, Token);

  Token.id[eid] = nextTokenId++;
  Token.baseX[eid] = x;
  Token.beltId[eid] = belt.id;

  Transform.posX[eid] = x;
  Transform.posY[eid] = 0;
  Transform.posZ[eid] = belt.z;

  const hidden = isInsideCube(x);
  Transform.scaleX[eid] = hidden ? 0 : 1;
  Transform.scaleY[eid] = hidden ? 0 : 1;
  Transform.scaleZ[eid] = hidden ? 0 : 1;

  Mesh.shape[eid] = 0;
  Mesh.sizeX[eid] = 0.3;
  Mesh.sizeY[eid] = 0.3;
  Mesh.sizeZ[eid] = 0.3;
  Mesh.color[eid] = belt.color;

  return eid;
}

function updateTokenVisibility(eid: number): void {
  const x = Transform.posX[eid];
  const hidden = isInsideCube(x);
  Transform.scaleX[eid] = hidden ? 0 : 1;
  Transform.scaleY[eid] = hidden ? 0 : 1;
  Transform.scaleZ[eid] = hidden ? 0 : 1;
}

function fillBeltToSpawn(state: State, belt: Belt): void {
  let currentSlide = 0;
  if (belt.isSliding) {
    const t = Math.min((elapsedTime - belt.slideStartTime) / belt.slideTime, 1);
    currentSlide = belt.slideAmount * easeOutExpo(t);
  }

  let rightmostPos = REMOVE_X;
  let rightmostBaseX = REMOVE_X;
  for (const eid of belt.tokens) {
    const pos = Transform.posX[eid];
    if (pos > rightmostPos) {
      rightmostPos = pos;
      rightmostBaseX = Token.baseX[eid];
    }
  }

  while (rightmostPos < SPAWN_X) {
    const newBaseX = rightmostBaseX + TOKEN_SPACING;
    const newPos = newBaseX - currentSlide;

    if (newPos > SPAWN_X) break;

    const eid = createToken(state, belt, newBaseX);
    belt.tokens.push(eid);

    Transform.posX[eid] = newPos;
    updateTokenVisibility(eid);

    rightmostBaseX = newBaseX;
    rightmostPos = newPos;
  }
}

function cleanupBelt(state: State, belt: Belt): void {
  belt.tokens = belt.tokens.filter((eid) => {
    if (Transform.posX[eid] < REMOVE_X) {
      state.removeEntity(eid);
      return false;
    }
    return true;
  });
}

function updateLabelPop(belt: Belt): void {
  if (belt.labelPopStartTime < 0) return;

  const t = Math.min((elapsedTime - belt.labelPopStartTime) / LABEL_POP_DURATION, 1);
  const scale = easeOutBack(t);

  Transform.scaleX[belt.labelEid] = scale;
  Transform.scaleY[belt.labelEid] = scale;
  Transform.scaleZ[belt.labelEid] = scale;
}

function updateBonusLabel(): void {
  if (bonusStartTime < 0 || bonusLabelEid < 0) return;

  const elapsed = elapsedTime - bonusStartTime;
  const totalDuration =
    BONUS_DELAY + BONUS_ANTICIPATION_DURATION + BONUS_POP_DURATION + BONUS_HOLD_DURATION + BONUS_FADE_DURATION;

  if (elapsed >= totalDuration) {
    Transform.scaleX[bonusLabelEid] = 0;
    Transform.scaleY[bonusLabelEid] = 0;
    Transform.scaleZ[bonusLabelEid] = 0;
    bonusStartTime = -1;
    return;
  }

  let scale: number;

  if (elapsed < BONUS_DELAY) {
    // Delay phase - stay hidden
    scale = 0;
  } else if (elapsed < BONUS_DELAY + BONUS_ANTICIPATION_DURATION) {
    // Anticipation phase - small wind-up scale
    const t = (elapsed - BONUS_DELAY) / BONUS_ANTICIPATION_DURATION;
    scale = 0.3 * t;
  } else if (elapsed < BONUS_DELAY + BONUS_ANTICIPATION_DURATION + BONUS_POP_DURATION) {
    // Pop in phase with subtle overshoot
    const t = (elapsed - BONUS_DELAY - BONUS_ANTICIPATION_DURATION) / BONUS_POP_DURATION;
    scale = 0.3 + 0.7 * easeOutBack(t);
  } else if (elapsed < BONUS_DELAY + BONUS_ANTICIPATION_DURATION + BONUS_POP_DURATION + BONUS_HOLD_DURATION) {
    // Hold phase
    scale = 1;
  } else {
    // Fade out phase
    const fadeT =
      (elapsed - BONUS_DELAY - BONUS_ANTICIPATION_DURATION - BONUS_POP_DURATION - BONUS_HOLD_DURATION) /
      BONUS_FADE_DURATION;
    scale = 1 - fadeT;
  }

  Transform.scaleX[bonusLabelEid] = scale;
  Transform.scaleY[bonusLabelEid] = scale;
  Transform.scaleZ[bonusLabelEid] = scale;
}

function updateBelt(state: State, belt: Belt, dt: number): void {
  belt.timer += dt;

  const interval = belt.getInterval();
  // Slide time scales with interval, capped at BASE_SLIDE_TIME
  const slideTime = Math.min(interval * 0.8, BASE_SLIDE_TIME);

  if (!belt.isSliding && belt.timer >= interval) {
    const count = belt.getTokenCount();
    belt.slideAmount = count * TOKEN_SPACING;
    belt.slideStartTime = elapsedTime;
    belt.slideTime = slideTime;
    belt.isSliding = true;
    belt.timer = 0;

    // Update label and trigger pop
    belt.lastTokenCount = count;
    Text.content[belt.labelEid] = `+${count}`;
    belt.labelPopStartTime = elapsedTime;

    // Trigger bonus animation when all K tokens accepted (k+1 total)
    if (belt.id === 1 && count === params.k + 1) {
      bonusStartTime = elapsedTime;
    }

    // Track token history for rolling average
    belt.tokenHistory.push({ time: elapsedTime, count });
  }

  // Prune old token history entries
  const cutoff = elapsedTime - RATE_WINDOW;
  while (belt.tokenHistory.length > 0 && belt.tokenHistory[0].time < cutoff) {
    belt.tokenHistory.shift();
  }

  // Calculate rate based on time span between oldest and newest emissions
  let rate = 0;
  if (belt.tokenHistory.length >= 2) {
    const totalTokens = belt.tokenHistory.reduce((sum, entry) => sum + entry.count, 0);
    const oldestTime = belt.tokenHistory[0].time;
    const newestTime = belt.tokenHistory[belt.tokenHistory.length - 1].time;
    const timeSpan = newestTime - oldestTime;
    if (timeSpan > 0) {
      // Subtract first emission's count since it marks the start of the window
      const firstCount = belt.tokenHistory[0].count;
      rate = (totalTokens - firstCount) / timeSpan;
    }
  }
  Text.content[belt.rateValueEid] = rate.toFixed(1);

  if (belt.isSliding) {
    const t = Math.min((elapsedTime - belt.slideStartTime) / belt.slideTime, 1);
    const currentSlide = belt.slideAmount * easeOutExpo(t);

    for (const eid of belt.tokens) {
      Transform.posX[eid] = Token.baseX[eid] - currentSlide;
      updateTokenVisibility(eid);
    }

    if (t >= 1) {
      for (const eid of belt.tokens) {
        Token.baseX[eid] = Transform.posX[eid];
      }
      belt.isSliding = false;
    }
  }

  updateLabelPop(belt);
  fillBeltToSpawn(state, belt);
  cleanupBelt(state, belt);
}

function initialize(state: State): void {
  const seqRateLabels = createRateLabels(state, 0, TARGET_TOKEN_COLOR);
  sequentialBelt = {
    id: 0,
    z: 0,
    color: TARGET_TOKEN_COLOR,
    tokens: [],
    getTokenCount: () => 1,
    getInterval: () => params.targetLatencyMs / 1000,
    timer: 0,
    isSliding: false,
    slideStartTime: 0,
    slideTime: BASE_SLIDE_TIME,
    slideAmount: 0,
    labelEid: createLabel(state, 0, TARGET_TOKEN_COLOR),
    lastTokenCount: 0,
    labelPopStartTime: -1,
    rateValueEid: seqRateLabels.valueEid,
    rateUnitEid: seqRateLabels.unitEid,
    tokenHistory: [],
  };

  const specRateLabels = createRateLabels(state, 2, SPEC_TOKEN_COLOR);
  speculativeBelt = {
    id: 1,
    z: 2,
    color: SPEC_TOKEN_COLOR,
    tokens: [],
    getTokenCount: sampleTokens,
    getInterval: () => (params.targetLatencyMs + params.k * params.draftLatencyMs) / 1000,
    timer: 0,
    isSliding: false,
    slideStartTime: 0,
    slideTime: BASE_SLIDE_TIME,
    slideAmount: 0,
    labelEid: createLabel(state, 2, SPEC_TOKEN_COLOR),
    lastTokenCount: 0,
    labelPopStartTime: -1,
    rateValueEid: specRateLabels.valueEid,
    rateUnitEid: specRateLabels.unitEid,
    tokenHistory: [],
  };

  bonusLabelEid = createBonusLabel(state);

  initialized = true;
}

const AnimationSystem: System = {
  group: 'simulation',
  update(state: State) {
    if (!initialized) {
      initialize(state);
    }

    const dt = Math.min(state.time.deltaTime, 0.05);
    elapsedTime += dt;

    updateBelt(state, sequentialBelt, dt);
    updateBelt(state, speculativeBelt, dt);
    updateBonusLabel();
  },
};

export const SpeedupPlugin: Plugin = {
  components: { Token },
  systems: [AnimationSystem],
};

export function setParams(newParams: Partial<typeof params>): void {
  Object.assign(params, newParams);
}
